import { Link } from 'react-router-dom';
import { MessageSquareText, Search, Scale, MapPin, ShieldCheck, Headset, Languages, ArrowRight, Sparkles, Phone, MessageCircle } from 'lucide-react';
import { EC_GREEN } from '@/components/ai/ChatShowcase';
import { openChatWidget } from '@/components/ai/openChatWidget';
import { useKiosk } from '@/components/ai/kioskContext';
import { EUROPCAR_DUBAI_URL } from '@/i18n/navData';

export const GREEN_BTN = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-6 py-3.5 font-heading font-bold text-sm text-white transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#159A47]';
export const GHOST_BTN = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-black/15 bg-white px-6 py-3.5 font-heading font-bold text-sm text-[#121212] transition-colors hover:border-[#159A47] hover:text-[#159A47]';

export function GreenChip({ children, testid }) {
  return <span data-testid={testid} className="inline-flex items-center gap-2 rounded-full bg-[#E8F5EC] px-3.5 py-1.5 font-heading font-bold text-[11px] tracking-[0.15em] uppercase" style={{ color: EC_GREEN }}>{children}</span>;
}

export function GreenHeader({ chip, title, desc, align = 'left' }) {
  const centered = align === 'center';
  return (
    <div className={centered ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}>
      {chip && <GreenChip>{chip}</GreenChip>}
      <h2 className="mt-4 font-heading font-bold text-3xl sm:text-4xl text-[#121212] tracking-tight leading-[1.1]">{title}</h2>
      {desc && <p className="mt-4 font-body text-base sm:text-lg text-[#666666] leading-relaxed">{desc}</p>}
    </div>
  );
}

export function CoBrandLockup({ light = false }) {
  return (
    <div data-testid="ai-cobrand" className="inline-flex items-center gap-4 sm:gap-5">
      <img src={light ? '/egmg-logo-transparent.png' : '/egmg-logo-dark-text.png'} alt="Eurogulf Mobility Group" className="h-9 sm:h-11 w-auto" />
      <span className={`font-heading font-black text-xl ${light ? 'text-white/50' : 'text-black/25'}`}>×</span>
      <img src="/europcar-logo.png" alt="Europcar Car Rental: Dubai & NE" className="h-7 sm:h-8 w-auto" />
    </div>
  );
}

const FEATURES = [
  { icon: MessageSquareText, title: 'Just describe your trip', desc: 'Arrival terminal, dates, passengers, luggage, budget. Tell the concierge in your own words and it does the searching for you.' },
  { icon: Search, title: 'Smart vehicle matching', desc: 'From compact city cars to 7-seat SUVs and executive sedans, it recommends the classes that genuinely fit how you will travel.' },
  { icon: Scale, title: 'Transparent pricing, explained', desc: 'Daily, weekly and monthly rates compared side by side, with insurance, deposit, mileage and VAT explained in plain language.' },
  { icon: MapPin, title: 'Every Europcar location', desc: 'All three Dubai Airport terminals, Atlantis The Palm, Emirates Towers, Dubai Hills Mall and more across Dubai and the Northern Emirates.' },
  { icon: ShieldCheck, title: 'Europcar standards, always', desc: 'The same ISO-certified fleet, 24/7 roadside assistance and Europcar service you have trusted in the UAE since 1978.' },
  { icon: Headset, title: 'A human is one tap away', desc: 'Ready to book, or prefer a person? The concierge hands you straight to the Europcar team, 24 hours a day.' },
];

export function FeatureSection() {
  return (
    <section data-testid="ai-features" className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <GreenHeader chip="What it feels like" title="Car rental that listens first and searches second." desc="No filters, no forms, no scrolling through pages of cars you do not need. Just a conversation that ends with the right vehicle waiting for you." />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px overflow-hidden rounded-2xl border border-black/[0.06] bg-black/[0.06]">
          {FEATURES.map((f) => (
            <div key={f.title} className="group bg-white p-7 transition-colors hover:bg-[#F1F8F3]">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#E8F5EC] transition-colors group-hover:bg-white" style={{ color: EC_GREEN }}><f.icon className="w-5 h-5" /></span>
              <h3 className="mt-5 font-heading font-bold text-lg text-[#121212]">{f.title}</h3>
              <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { n: '01', title: 'Tell it your trip', desc: 'Type or tap. Where you land, how long you stay, who is travelling and what matters most to you.' },
  { n: '02', title: 'See the cars that fit', desc: 'The concierge shortlists vehicle classes with clear all-in pricing, availability at your pickup point and the extras you asked for.' },
  { n: '03', title: 'Reserve, or hand over', desc: 'Confirm your choice in the chat or pass the conversation to a Europcar agent who picks up exactly where you left off.' },
];

export function HowItWorks() {
  return (
    <section data-testid="ai-how-it-works" className="border-y border-black/5 bg-[#FAFAFA] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <GreenHeader align="center" chip="How it works" title="Three steps from touchdown to keys in hand." desc="Built on the same booking engine and fleet that power Europcar Car Rental: Dubai & NE, so what the concierge offers is what is really on the lot." />
        <ol className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.n} className="relative rounded-3xl border border-black/[0.06] bg-white p-7 text-center">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full font-heading font-black text-xl text-white shadow-[0_10px_24px_rgba(21,154,71,0.35)]" style={{ backgroundColor: EC_GREEN }}>{i + 1}</span>
              <h3 className="mt-5 font-heading font-bold text-lg text-[#121212]">{s.title}</h3>
              <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{s.desc}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10 text-center">
          <button type="button" data-testid="ai-how-cta" onClick={openChatWidget} className={GREEN_BTN} style={{ backgroundColor: EC_GREEN }}><Sparkles className="w-4 h-4" /> Try it now</button>
        </div>
      </div>
    </section>
  );
}

export function CustomerFirst() {
  return (
    <section data-testid="ai-customer-first" className="bg-white py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <GreenChip>Customer-first AI</GreenChip>
        <p className="mt-6 font-heading font-bold text-2xl sm:text-4xl text-[#121212] tracking-tight leading-[1.15]">
          "For fifty years we have moved people across the UAE by listening to them. The AI Car Rental Concierge is that same promise, delivered the moment a customer starts typing."
        </p>
        <p className="mt-6 font-body text-sm text-[#666666]">Eurogulf Mobility Group · Europcar Car Rental: Dubai &amp; NE</p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <GreenChip><Languages className="w-3.5 h-3.5" /> English &amp; Arabic</GreenChip>
          <GreenChip><Headset className="w-3.5 h-3.5" /> 24/7 human hand-off</GreenChip>
          <GreenChip><ShieldCheck className="w-3.5 h-3.5" /> Europcar service standards</GreenChip>
        </div>
      </div>
    </section>
  );
}

export function AtmBand() {
  const kiosk = useKiosk();
  return (
    <section data-testid="ai-atm-band" className="relative overflow-hidden py-16 sm:py-24" style={{ backgroundColor: '#0E6B33' }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)', backgroundSize: '28px 28px' }} aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.12] px-3.5 py-1.5 font-heading font-bold text-[11px] tracking-[0.15em] uppercase text-white">Arabian Travel Market 2026</span>
          <h2 className="mt-5 font-heading font-bold text-3xl sm:text-5xl text-white tracking-tight leading-[1.05]">{kiosk ? 'You are here. Try it yourself.' : 'See it live. Try it yourself.'}</h2>
          <p className="mt-5 max-w-2xl font-body text-base sm:text-lg text-white/80 leading-relaxed">{kiosk
            ? 'Start a conversation right on this screen and discover what truly AI assisted car rental feels like. Our Europcar team is beside you if you would like to keep the conversation going on your own phone.'
            : 'We are launching the AI Car Rental Concierge at Arabian Travel Market 2026. Walk up, start a conversation and discover what truly AI assisted car rental feels like, then keep the conversation going on your own phone.'}</p>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
          <button type="button" data-testid="ai-atm-cta" onClick={openChatWidget} className={`${GREEN_BTN} bg-white !text-[#0E6B33] w-full lg:w-auto`}><MessageSquareText className="w-4 h-4" /> Start a conversation</button>
          {!kiosk && <Link to="/europcar" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3.5 font-heading font-bold text-sm text-white transition-colors hover:bg-white/[0.12] w-full lg:w-auto">About Europcar Car Rental <ArrowRight className="w-4 h-4" /></Link>}
        </div>
      </div>
    </section>
  );
}

export function HumanFallback() {
  const kiosk = useKiosk();
  if (kiosk) {
    return (
      <section data-testid="ai-final-cta" className="bg-white py-14 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#121212] tracking-tight">Prefer to talk to a person?</h2>
          <p className="mt-3 font-body text-base text-[#666666]">The Europcar Car Rental: Dubai &amp; NE team is right here at the stand. Just ask, or start with the concierge and hand over any time.</p>
          <div className="mt-8 flex justify-center">
            <button type="button" data-testid="ai-kiosk-chat-cta" onClick={openChatWidget} className={GREEN_BTN} style={{ backgroundColor: EC_GREEN }}><Sparkles className="w-4 h-4" /> Start a conversation</button>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section data-testid="ai-final-cta" className="bg-white py-14 sm:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#121212] tracking-tight">Prefer to talk to a person?</h2>
        <p className="mt-3 font-body text-base text-[#666666]">The Europcar team is available around the clock, on the phone, on WhatsApp or at any of our 14 locations.</p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href="tel:80038767227" data-testid="ai-call-cta" className={GHOST_BTN}><Phone className="w-4 h-4" /> Call 800 EUROPCAR</a>
          <a href="https://wa.me/97180038767227?text=Hi%20Europcar%2C%20I%27d%20like%20some%20help%20with%20a%20car%20rental." target="_blank" rel="noopener noreferrer" data-testid="ai-whatsapp-cta" className={GHOST_BTN}><MessageCircle className="w-4 h-4" /> WhatsApp Europcar</a>
          <a href={EUROPCAR_DUBAI_URL} target="_blank" rel="noopener noreferrer" data-testid="ai-visit-europcar" className={GHOST_BTN}>Visit Europcar Dubai <ArrowRight className="w-4 h-4" /></a>
        </div>
      </div>
    </section>
  );
}
